import { GtagEvent } from '@/app/ga';
import logger from '@/utils/logger';
import { create } from 'zustand';
import * as productService from '../services/productService';
import { OrderingField, OrderingState } from '../types/api/product';
import { mapFilterApiToModel } from '../types/mappers/filter';
import { mapProductsApiToModel } from '../types/mappers/product';
import { Product } from '../types/models/product';
import { useFilterStore } from './filterStore';

const PAGE_SIZE = 10;

interface ProductState {
  // 상태
  products: Product[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  error: Error | null;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  ordering: OrderingState;
  pageSize: number;

  // 액션
  setProducts: (products: Product[]) => void;
  setTotalCount: (count: number) => void;
  setTotalPages: (pages: number) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  setCurrentPage: (page: number) => void;
  setHasNextPage: (hasNext: boolean) => void;
  setHasPreviousPage: (hasPrevious: boolean) => void;
  toggleOrdering: (field: OrderingField) => void;
  pageChange: (page: number) => void;
  toggleCart: (productId: number) => void;
  setPageSize: (pageSize: number) => void;

  // API 액션
  fetchProducts: (
    filters?: Record<string, string>,
    page?: number,
    removeQueryFilters?: boolean,
  ) => Promise<void>;

  fetchSearchProducts: (
    searchTerm: string,
    filters?: Record<string, string>,
  ) => Promise<void>;

  clearSearchProducts: () => Promise<void>;
}

export const useProductStore = create<ProductState>((set, get) => ({
  // 초기 상태
  products: [],
  totalCount: 0,
  totalPages: 0,
  isLoading: true,
  error: null,
  currentPage: 1,
  hasNextPage: false,
  hasPreviousPage: false,
  ordering: { field: 'unit_price', direction: 'asc' },
  pageSize: PAGE_SIZE,
  searchTerm: '',
  // 액션
  setProducts: (products) => set({ products }),
  setTotalCount: (totalCount) => set({ totalCount }),
  setTotalPages: (totalPages) => set({ totalPages }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setCurrentPage: (currentPage) => set({ currentPage }),
  setHasNextPage: (hasNextPage) => set({ hasNextPage }),
  setHasPreviousPage: (hasPreviousPage) => set({ hasPreviousPage }),

  toggleOrdering: (field: OrderingField) => {
    const currentOrdering = get().ordering;
    const newOrdering: OrderingState = {
      field,
      direction:
        currentOrdering.field === field && currentOrdering.direction === 'asc'
          ? 'desc'
          : 'asc',
    };

    set({ ordering: newOrdering });
    get().fetchProducts(useFilterStore.getState().queryFilters);
  },

  setPageSize: (pageSize) => {
    set({ pageSize });
    get().fetchProducts(useFilterStore.getState().queryFilters);
  },

  pageChange: (page: number) => {
    set({ currentPage: page });
    get().fetchProducts(useFilterStore.getState().queryFilters, page);
  },

  toggleCart: (productId: number) => {
    const products = get().products;
    const product = products.find((product) => product.id === productId);
    if (product) {
      product.is_in_cart = !product.is_in_cart;
    }
    set({ products });
  },

  // API 액션
  fetchProducts: async (
    filters?: Record<string, string>,
    page = 1,
    removeQueryFilters = false,
  ) => {
    logger.info('>> fetchProducts', page, filters);

    const { ordering, pageSize } = get();
    set({ isLoading: true, error: null });

    const { EST, brand, grade, meat_loin, meat_type, origin, storage_type, warehouse } = { ...filters };

    if (EST) {
      const ests = EST.split(',');
      ests.forEach((est) => {
        GtagEvent('filter_applied', {
          filter: 'EST',
          value: est,
        });
      });
    }

    if (brand) {
      const brands = brand.split(',');
      brands.forEach((brand) => {
        GtagEvent('filter_applied', {
          filter: 'brand',
          value: brand,
        });
      });
    }

    if (grade) {
      const grades = grade.split(',');
      grades.forEach((grade) => {
        GtagEvent('filter_applied', {
          filter: 'grade',
          value: grade,
        });
      });
    }

    if (meat_loin) {
      const meatLoins = meat_loin.split(',');
      meatLoins.forEach((meatLoin) => {
        GtagEvent('filter_applied', {
          filter: 'meat_loin',
          value: meatLoin,
        });
      });
    }

    if (storage_type) {
      const storageTypes = storage_type.split(',');
      storageTypes.forEach((storageType) => {
        GtagEvent('filter_applied', {
          filter: 'storage_type',
          value: storageType,
        });
      });
    }

    if (meat_type) {
      const meatTypes = meat_type.split(',');
      meatTypes.forEach((meatType) => {
        GtagEvent('filter_applied', {
          filter: 'meat_type',
          value: meatType,
        });
      });
    }

    if (origin) {
      const origins = origin.split(',');
      origins.forEach((origin) => {
        GtagEvent('filter_applied', {
          filter: 'origin',
          value: origin,
        });
      });
    }

    if (warehouse) {
      const warehouses = warehouse.split(',');
      warehouses.forEach((warehouse) => {
        GtagEvent('filter_applied', {
          filter: 'warehouse',
          value: warehouse,
        });
      });
    }

    return await productService
      .getProducts(page, pageSize, ordering, filters)
      .then((response) => {
        const products = mapProductsApiToModel(response.results);
        set({
          products,
          totalCount: response.count,
          currentPage: page,
          totalPages: Math.ceil(response.count / pageSize),
          hasNextPage: !!response.next,
          hasPreviousPage: !!response.previous,
          isLoading: false,
        });

        useFilterStore.getState().setQueryFilters(filters ?? {});

        if (useFilterStore.getState().filters.length === 0) {
          if (removeQueryFilters && filters) {
            const filteredResponse = mapFilterApiToModel(response).filter(
              (f) => !filters[f.id],
            );
            useFilterStore.getState().setFilters(filteredResponse);
          } else {
            useFilterStore.getState().setFilters(mapFilterApiToModel(response));
          }
        }
      });
  },

  fetchSearchProducts: async (
    searchTerm: string,
    filters?: Record<string, string>,
  ) => {
    useFilterStore.getState().setFilters(null);

    if (filters) {
      return await get().fetchProducts(filters, 1, true);
    } else {
      return await get().fetchProducts({ search: searchTerm });
    }
  },

  clearSearchProducts: async () => {
    logger.info('>> clearSearchProducts');
    useFilterStore.getState().setFilters(null);
    useFilterStore.getState().setQueryFilters({});
    return await get().fetchProducts({}, 1, false);
  },
}));
